import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AdminContextType {
  isAdmin: boolean;
  isPasswordSet: boolean;
  login: (password: string) => boolean;
  logout: () => void;
  setPassword: (password: string) => boolean;
  changePassword: (oldPassword: string, newPassword: string) => boolean;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export function AdminProvider({ children }: { children: ReactNode }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPasswordSet, setIsPasswordSet] = useState(false);

  useEffect(() => {
    // Check if password is set
    const storedPassword = localStorage.getItem('admin_password');
    setIsPasswordSet(!!storedPassword);

    // Check if admin is logged in (session)
    const adminSession = sessionStorage.getItem('admin_session');
    if (adminSession === 'true' && storedPassword) {
      setIsAdmin(true);
    }
  }, []);

  const setPassword = (password: string): boolean => {
    if (password.length !== 4 || !/^\d{4}$/.test(password)) {
      return false;
    }
    localStorage.setItem('admin_password', password);
    setIsPasswordSet(true);
    setIsAdmin(true);
    sessionStorage.setItem('admin_session', 'true');
    return true;
  };

  const login = (password: string): boolean => {
    const storedPassword = localStorage.getItem('admin_password');
    if (storedPassword === password) {
      setIsAdmin(true);
      sessionStorage.setItem('admin_session', 'true');
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAdmin(false);
    sessionStorage.removeItem('admin_session');
  };

  const changePassword = (oldPassword: string, newPassword: string): boolean => {
    const storedPassword = localStorage.getItem('admin_password');
    if (storedPassword !== oldPassword) {
      return false;
    }
    if (newPassword.length !== 4 || !/^\d{4}$/.test(newPassword)) {
      return false;
    }
    localStorage.setItem('admin_password', newPassword);
    return true;
  };

  return (
    <AdminContext.Provider value={{ isAdmin, isPasswordSet, login, logout, setPassword, changePassword }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}
